import React, { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GroupsContext } from '../../context/group/GroupContext';
import Message from '../../components/ui/Message';
import { Group, User } from '../../models/interfaces';
import { UsersContext } from '../../context/users/UserContext';

interface LocationState {
  user: User;
}

export const UserGroupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = location.state as LocationState;

  const {
    loadGroups,
    loadGroupByUser,
    saveGroupByUser
  } = useContext(UsersContext);

  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  /* =========================
     Cargar grupos
  ========================== */

  useEffect(() => {
    setLoading(true);

    Promise.all([
      loadGroups(),
      loadGroupByUser(user.user_id)
    ])
      .then(([allGroups, userGroups]) => {
        setGroups(allGroups);
        setSelectedGroups(userGroups.map(g => g.group_id));
      })
      .finally(() => setLoading(false));
  }, []);

  /* =========================
     Toggle grupo
  ========================== */

  const handleToggleGroup = (groupId: number) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  /* =========================
     Guardar
  ========================== */

  const handleSave = async () => {
    const payload = {
      user_id: user.user_id,
      groups: selectedGroups
    };

    const resp = await saveGroupByUser(payload);

    if (resp === 'success') {
      setSuccessMessage('Grupos asignados correctamente.');
    } else {
      setErrorMessage('Ocurrió un error asignando los grupos.');
    }
  };

  const removeError = () => setErrorMessage('');
  const removeSuccess = () => {
    setSuccessMessage('');
    navigate(-1);
  };

  /* =========================
     Render
  ========================== */

  return (
    <div className="mt-5 container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5>
          Grupos del usuario:{' '}
          <span className="fw-bold">
            {user.name} {user.lastname}
          </span>
        </h5>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="text-center mt-4">
          <span className="spinner-border" />
        </div>
      ) : (
        <table className="table table-hover table-striped">
          <thead className="thead-dark">
            <tr>
              <th style={{ width: 120, textAlign: 'center' }}>
                Asignado
              </th>
              <th>Grupo</th>
            </tr>
          </thead>

          <tbody>
            {groups.length > 0 ? (
              groups.map(group => (
                <tr key={group.group_id}>
                  <td className="text-center">
                    <input
                      type="checkbox"
                      checked={selectedGroups.includes(group.group_id)}
                      onChange={() =>
                        handleToggleGroup(group.group_id)
                      }
                    />
                  </td>
                  <td>{group.group_name}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="text-center">
                  No existen grupos.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {/* Acciones */}
      <div className="d-flex justify-content-center mt-4">
        <button
          className="btn btn-outline-secondary me-5"
          onClick={() => navigate(-1)}
        >
          Volver
        </button>

        <button
          className="btn btn-success"
          onClick={handleSave}
        >
          Guardar cambios
        </button>
      </div>

      {/* Mensajes */}
      {errorMessage && (
        <Message
          type="error"
          message={errorMessage}
          closeDialog={removeError}
          handleOk={removeError}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}

      {successMessage && (
        <Message
          type="success"
          title="Éxito"
          message={successMessage}
          closeDialog={removeSuccess}
          handleOk={removeSuccess}
          backgroundColor="rgba(0,0,0,0.4)"
        />
      )}
    </div>
  );
};
